/**
 * MobilePostReview Component for LOOPINO AI Platform
 * Main mobile component for reviewing and approving social media posts
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './MobilePostReview.css';
import PostCarousel from './PostCarousel';
import PostInfo from './PostInfo';
import AIRegenerationModal from './AIRegenerationModal';
import TextEditModal from './TextEditModal';
import PostVersions from './PostVersions';
import swipeBackIcon from '../../../assets/images/icons/icon-swipe-back.svg';
import MobileThreadsBackground from '../MobileThreadsBackground';
import { useMobileSwipe } from '../../../hooks/useMobileSwipe';
import { useAIRegeneration } from '../../../hooks/useAIRegeneration';
import useMobilePostReview from '../../../hooks/useMobilePostReview.js';
import { getFirstMediaUrl, getAllMediaUrls } from '../../../utils/mediaHelpers.js';
import campaignService from '../../../services/CampaignService.js';
import { Skeleton } from '../../effects';
import useCompany from '../../../hooks/useCompany';
import { hasGeneratedDna } from '../../../utils/companyDna';
import API_CONFIG from '../../../config/api';
import { useAuthContext } from '../../../context/AuthContext';
import useLanguage from '../../../hooks/useLanguage';
import { useCapabilities } from '../../../capabilities/CapabilityContext';
import {
  SUPPORTED_MEDIA_ACCEPT,
  SUPPORTED_MEDIA_LABEL,
  isSupportedMediaFile
} from '../mediaUploadConfig';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';
import { apiFetch, parseResponseBody } from '../../../utils/http';
import logger from '../../../utils/clientLogger';

// Posts are now managed at App level and passed as props

const DOCK_HOME_PRIMARY_EVENT = 'mobileDockHomePrimaryRequest';

const DEFAULT_CAROUSEL_ASPECT_RATIO = 5 / 4; // Matches legacy 4:5 layout (height:width)
const MAX_VERTICAL_ASPECT_RATIO = 4 / 3; // Clamp overly tall media
const FALLBACK_PADDING_PERCENT = `${DEFAULT_CAROUSEL_ASPECT_RATIO * 100}%`;
const DEFAULT_CAROUSEL_STYLE = Object.freeze({
  '--mobile-carousel-aspect-padding': FALLBACK_PADDING_PERCENT
});
const VIDEO_EXTENSIONS = /\.(mp4|mov|m4v|webm|ogg|ogv|avi|mkv)$/i;

const buildCarouselAspectData = (sourceUrl, aspectRatio, { isFallback = false } = {}) => {
  const safeAspect = Number.isFinite(aspectRatio) && aspectRatio > 0
    ? aspectRatio
    : DEFAULT_CAROUSEL_ASPECT_RATIO;
  const clampedAspect = Math.min(safeAspect, MAX_VERTICAL_ASPECT_RATIO);
  const paddingBottom = `${(clampedAspect * 100).toFixed(4)}%`;

  return {
    aspectRatio: clampedAspect,
    paddingBottom,
    sourceUrl: sourceUrl || null,
    isFallback,
    style: Object.freeze({
      '--mobile-carousel-aspect-padding': paddingBottom
    })
  };
};

const normalizePostIdValue = (value) => {
  if (value === undefined || value === null) {
    return null;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'object') {
    if (value.$oid) {
      return String(value.$oid);
    }

    if (value.id || value._id) {
      return normalizePostIdValue(value.id ?? value._id);
    }

    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return String(stringified);
      }
    }
  }

  return null;
};

const getPostIdentifier = (postish) => {
  if (!postish) {
    return null;
  }

  if (typeof postish === 'string' || typeof postish === 'number') {
    return String(postish);
  }

  return (
    normalizePostIdValue(postish.id) ||
    normalizePostIdValue(postish._id) ||
    normalizePostIdValue(postish.post_id) ||
    normalizePostIdValue(postish.postId) ||
    null
  );
};

const MobilePostReview = ({
  posts: propPosts,
  currentPostIndex: propCurrentIndex = undefined, // undefined = let component auto-select
  targetPostId = null,
  preferPropPosts = false,
  campaignId, // New prop for campaign filtering
  onPostChange,
  onPostApprove,
  onPostReject,
  onPostDelete,
  onPostRegenerate,
  onPostTextEdit,
  onImageUpload,
  onEditPost,
  onCampaignClick, // Callback per navigare alla pagina della campagna
  onThumbnailClick, // Callback per navigare al calendario cliccando il thumbnail
  onNavigateToDna, // Callback per navigare al DNA aziendale
  onNavigateToImageGenerator, // Callback per navigare a Crea post
  onOpenNewCampaignModal, // Callback per aprire la nuova campagna
  onCreateFirstCampaign, // Callback per creare la prima campagna
  isLoading: propIsLoading = false,
  onHomeReady
}) => {
  const { logout: authLogout } = useAuthContext();
  const { t } = useLanguage();
  const { notify } = useMobileAlert();
  const unsupportedFormatMessage = useMemo(
    () => t('postReview.postEdit.media.unsupportedFormat', { defaultValue: `Formato non supportato. Usa: ${SUPPORTED_MEDIA_LABEL}` }),
    [t]
  );

  // API hook for post management
  const {
    posts: apiPosts,
    isLoading: apiIsLoading,
    isRegenerating: apiIsRegenerating,
    error: apiError,
    approvePost: apiApprovePost,
    rejectPost: apiRejectPost,
    regeneratePost: apiRegeneratePost,
    editPostText: apiEditPostText,
    handleImageUpload: apiHandleImageUpload,
    editPost: apiEditPost,
    deletePost: apiDeletePost,
    refresh: apiRefresh,
    clearError: apiClearError
    // Note: applyPostVersion is not used - version switching is local-only
  } = useMobilePostReview(campaignId);

  // State management
  const [posts, setPosts] = useState([]);
  const [currentPostIndex, setCurrentPostIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCarouselSwiping, setIsCarouselSwiping] = useState(false);
  const [postCampaignNames, setPostCampaignNames] = useState({});
  const campaignNameCacheRef = useRef(new Map());
  
  // NEW: Enhanced animation states
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [targetPostIndex, setTargetPostIndex] = useState(null);
  const [isImmediateTransition, setIsImmediateTransition] = useState(false);
  const [dragOffsetPercent, setDragOffsetPercent] = useState(0);
  const [releaseOffsetPercent, setReleaseOffsetPercent] = useState(0);
  const [transitionProgress, setTransitionProgress] = useState(0);

  // Shared image loading state for blur-on-load effect
  const [imageLoadingStates, setImageLoadingStates] = useState(() => new Map());

  const setImageLoadingState = useCallback((key, status) => {
    if (!key) return;
    setImageLoadingStates(prev => {
      const current = prev.get(key);
      if (current === status) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, status);
      return next;
    });
  }, [setImageLoadingStates]);

  const getImageLoadingProps = useCallback((source, scope = 'global') => {
    const normalizedScope = scope || 'global';
    const baseKey = typeof source === 'string' && source.length > 0
      ? source
      : 'unknown';
    const mapKey = `${normalizedScope}::${baseKey}`;
    const status = imageLoadingStates.get(mapKey) || 'loading';

    const statusClass = status === 'loaded'
      ? 'is-ready'
      : status === 'error'
        ? 'is-error'
        : 'is-loading';

    return {
      status,
      className: statusClass,
      onLoad: () => setImageLoadingState(mapKey, 'loaded'),
      onError: () => setImageLoadingState(mapKey, 'error')
    };
  }, [imageLoadingStates, setImageLoadingState]);

  // Carousel aspect ratio management (first media establishes frame)
  const mediaDimensionsCacheRef = useRef(new Map());
  const postCarouselAspectCacheRef = useRef(new Map());
  const [postCarouselAspects, setPostCarouselAspects] = useState({});
  const carouselStyleElementRef = useRef(null);

  // Post versions management
  const [postVersions, setPostVersions] = useState({}); // { postId: [versions] }
  const postVersionsRef = useRef({});
  const updatePostVersionsState = useCallback((updater) => {
    setPostVersions(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      postVersionsRef.current = next;
      return next;
    });
  }, []);

  const getMediaDimensions = useCallback((url) => {
    if (!url) {
      return Promise.reject(new Error('Missing media url'));
    }

    const cached = mediaDimensionsCacheRef.current.get(url);
    if (cached) {
      return Promise.resolve(cached);
    }

    if (typeof window === 'undefined') {
      const fallbackDimensions = { width: 1, height: DEFAULT_CAROUSEL_ASPECT_RATIO };
      mediaDimensionsCacheRef.current.set(url, fallbackDimensions);
      return Promise.resolve(fallbackDimensions);
    }

    const sanitizeUrl = (value) => {
      if (typeof value !== 'string') {
        return '';
      }
      const [withoutQuery] = value.split('?');
      const [withoutHash] = withoutQuery.split('#');
      return withoutHash || '';
    };

    const normalizedUrl = sanitizeUrl(url);
    const treatAsVideo = (() => {
      if (normalizedUrl.startsWith('data:')) {
        return normalizedUrl.startsWith('data:video');
      }
      if (normalizedUrl.startsWith('blob:')) {
        return false;
      }
      return VIDEO_EXTENSIONS.test(normalizedUrl);
    })();

    return new Promise((resolve, reject) => {
      let settled = false;

      const resolveWithCache = (dimensions) => {
        if (settled) {
          return;
        }
        settled = true;
        const normalizedDimensions = {
          width: Math.max(dimensions?.width || 1, 1),
          height: Math.max(dimensions?.height || 1, 1)
        };
        mediaDimensionsCacheRef.current.set(url, normalizedDimensions);
        resolve(normalizedDimensions);
      };

      const rejectOnce = (error) => {
        if (settled) {
          return;
        }
        settled = true;
        reject(error instanceof Error ? error : new Error('Unable to resolve media dimensions'));
      };

      if (treatAsVideo) {
        try {
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.playsInline = true;
          video.muted = true;

          const cleanup = () => {
            video.removeEventListener('loadedmetadata', handleLoadedMetadata);
            video.removeEventListener('error', handleError);
            try {
              video.pause();
            } catch (pauseError) {
              // noop
            }
            video.src = '';
          };

          const handleLoadedMetadata = () => {
            const dimensions = {
              width: video.videoWidth || 1,
              height: video.videoHeight || 1
            };
            cleanup();
            resolveWithCache(dimensions);
          };

          const handleError = (event) => {
            cleanup();
            rejectOnce(event?.error || new Error('Video metadata unavailable'));
          };

          video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
          video.addEventListener('error', handleError, { once: true });
          video.src = url;
          video.load();
        } catch (error) {
          rejectOnce(error);
        }
      } else {
        const image = new Image();
        image.decoding = 'async';

        const cleanup = () => {
          image.onload = null;
          image.onerror = null;
        };

        image.onload = () => {
          const dimensions = {
            width: image.naturalWidth || image.width || 1,
            height: image.naturalHeight || image.height || 1
          };
          cleanup();
          resolveWithCache(dimensions);
        };

        image.onerror = (event) => {
          cleanup();
          rejectOnce(event?.error || new Error('Image metadata unavailable'));
        };

        image.src = url;

        if (image.complete && (image.naturalWidth || image.naturalHeight)) {
          image.onload?.();
        }
      }
    });
  }, []);

  const ensureCarouselAspectForPost = useCallback((post) => {
    if (!post) {
      return undefined;
    }

    const postId = getPostIdentifier(post);
    if (!postId) {
      return undefined;
    }

    const mediaUrls = getAllMediaUrls(post) || [];
    if (mediaUrls.length === 0) {
      const fallbackData = buildCarouselAspectData(null, DEFAULT_CAROUSEL_ASPECT_RATIO, { isFallback: true });
      postCarouselAspectCacheRef.current.set(postId, fallbackData);
      setPostCarouselAspects(prev => {
        const existing = prev[postId];
        if (existing && existing.paddingBottom === fallbackData.paddingBottom) {
          return prev;
        }
        return { ...prev, [postId]: fallbackData };
      });
      return undefined;
    }

    const firstMediaUrl = mediaUrls[0];
    const cached = postCarouselAspectCacheRef.current.get(postId);
    if (cached && cached.sourceUrl === firstMediaUrl) {
      setPostCarouselAspects(prev => {
        if (prev[postId]) {
          return prev;
        }
        return { ...prev, [postId]: cached };
      });
      return undefined;
    }

    let isCancelled = false;

    getMediaDimensions(firstMediaUrl)
      .then(dimensions => {
        if (isCancelled) return;
        const aspectRatio = (dimensions.height || 1) / (dimensions.width || 1);
        const data = buildCarouselAspectData(firstMediaUrl, aspectRatio, { isFallback: false });
        postCarouselAspectCacheRef.current.set(postId, data);
        setPostCarouselAspects(prev => {
          const existing = prev[postId];
          if (existing && existing.sourceUrl === data.sourceUrl && existing.paddingBottom === data.paddingBottom) {
            return prev;
          }
          return { ...prev, [postId]: data };
        });
      })
      .catch(error => {
        if (isCancelled) return;
        logger.warn('Unable to resolve media dimensions for post', { postId, error, kind: 'user' });
        const fallbackData = buildCarouselAspectData(firstMediaUrl, DEFAULT_CAROUSEL_ASPECT_RATIO, { isFallback: true });
        postCarouselAspectCacheRef.current.set(postId, fallbackData);
        setPostCarouselAspects(prev => {
          return { ...prev, [postId]: fallbackData };
        });
      });

    return () => {
      isCancelled = true;
    };
  }, [getMediaDimensions]);

  const getCarouselAspectForPost = useCallback((post) => {
    if (!post) return null;
    const postId = getPostIdentifier(post);
    if (!postId) return null;
    return postCarouselAspects[postId] || postCarouselAspectCacheRef.current.get(postId) || null;
  }, [postCarouselAspects]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    if (carouselStyleElementRef.current) {
      return () => {};
    }

    const styleElement = document.createElement('style');
    styleElement.setAttribute('data-mobile-carousel-dynamic', 'true');
    styleElement.textContent = `
      .mobile-carousel-responsive .mobile-post-image-container {
        padding-bottom: var(--mobile-carousel-aspect-padding, ${FALLBACK_PADDING_PERCENT});
        max-height: var(--mobile-carousel-max-height, none);
      }
      .mobile-carousel-responsive .mobile-post-image,
      .mobile-carousel-responsive .mobile-post-video {
        object-fit: var(--mobile-carousel-media-fit, cover);
      }
    `;

    document.head.appendChild(styleElement);
    carouselStyleElementRef.current = styleElement;

    return () => {
      if (carouselStyleElementRef.current?.parentNode) {
        carouselStyleElementRef.current.parentNode.removeChild(carouselStyleElementRef.current);
      }
      carouselStyleElementRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!posts || posts.length === 0) {
      return undefined;
    }

    const cleanupFns = [];
    const indicesToPrepare = new Set([
      currentPostIndex,
      currentPostIndex - 1,
      currentPostIndex + 1
    ]);

    indicesToPrepare.forEach((index) => {
      if (index < 0 || index >= posts.length) {
        return;
      }
      const candidatePost = posts[index];
      if (!candidatePost) {
        return;
      }
      const cleanup = ensureCarouselAspectForPost(candidatePost);
      if (typeof cleanup === 'function') {
        cleanupFns.push(cleanup);
      }
    });

    return () => {
      cleanupFns.forEach(fn => {
        try {
          fn();
        } catch (error) {
          // noop
        }
      });
    };
  }, [posts, currentPostIndex, ensureCarouselAspectForPost]);

  const getPostScheduledTimestamp = useCallback((post) => {
    if (!post) return null;

    const rawValue = post?.scheduledDate ||
      post?.scheduled_time ||
      post?.scheduledTime ||
      post?.scheduled_at ||
      post?.scheduledAt;

    if (!rawValue) {
      return null;
    }

    try {
      const normalized = typeof rawValue === 'object' && rawValue.$date
        ? rawValue.$date
        : rawValue;
      const timestamp = new Date(normalized).getTime();
      return Number.isFinite(timestamp) ? timestamp : null;
    } catch (error) {
      return null;
    }
  }, []);

  const getVersionIdentifier = useCallback((version) => {
    return (
      version?.version_id ||
      version?.id ||
      version?._id ||
      version?.version ||
      version?.version_number ||
      null
    );
  }, []);

  const getVersionTimestamp = useCallback((version) => {
    const timestamp = version?.timestamp ||
      version?.created_at ||
      version?.createdAt ||
      version?.generated_at ||
      version?.generatedAt;

    if (!timestamp) return null;

    try {
      const value = typeof timestamp === 'object' && timestamp.$date
        ? timestamp.$date
        : timestamp;
      const time = new Date(value).getTime();
      return Number.isFinite(time) ? time : null;
    } catch (error) {
    logger.error('Failed to parse version timestamp', error, { kind: 'user' });
      return null;
    }
  }, []);

  const sortVersionsDescending = useCallback((versions) => {
    if (!Array.isArray(versions)) return [];

    return [...versions].sort((a, b) => {
      const timeA = getVersionTimestamp(a);
      const timeB = getVersionTimestamp(b);

      if (timeA !== null && timeB !== null && timeA !== timeB) {
        return timeB - timeA; // Newest first
      }

      if (timeA !== null && timeB === null) return -1;
      if (timeA === null && timeB !== null) return 1;

      const identifierA = getVersionIdentifier(a);
      const identifierB = getVersionIdentifier(b);

      if (identifierA !== null && identifierB !== null && identifierA !== identifierB) {
        if (typeof identifierA === 'number' && typeof identifierB === 'number') {
          return identifierB - identifierA;
        }
        return String(identifierB).localeCompare(String(identifierA));
      }

      return 0;
    });
  }, [getVersionTimestamp, getVersionIdentifier]);

  const [currentVersionIndices, setCurrentVersionIndices] = useState({}); // { postId: versionIndex }
  const [definitiveVersionIndices, setDefinitiveVersionIndices] = useState({}); // { postId: versionIndex } - definitive versions for publication
  const [isSettingDefinitiveVersion, setIsSettingDefinitiveVersion] = useState(false); // Loading state for setting definitive version

  const hasUpcomingPosts = useMemo(() => {
    if (!posts || posts.length === 0) {
      return false;
    }

    const now = Date.now();
    return posts.some(post => {
      const timestamp = getPostScheduledTimestamp(post);
      return typeof timestamp === 'number' && timestamp > now;
    });
  }, [posts, getPostScheduledTimestamp]);

  const showNoUpcomingPlaceholder = !!(posts && posts.length > 0 && !hasUpcomingPosts);

  const totalSlides = useMemo(() => {
    return posts.length + (showNoUpcomingPlaceholder ? 1 : 0);
  }, [posts.length, showNoUpcomingPlaceholder]);

  // AI Modal states
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalType, setAiModalType] = useState('full'); // 'image', 'text', 'full'
  const [aiModalImageIndex, setAiModalImageIndex] = useState(null);

  // Text Edit Modal states
  const [textEditModalOpen, setTextEditModalOpen] = useState(false);
  const [isTextSaving, setIsTextSaving] = useState(false);

  const containerRef = useRef(null);
  const initializedRef = useRef(false);
  const transitionAnimationRef = useRef(null);
  const transitionConfigRef = useRef(null);
  const releaseAnimationRef = useRef(null);
  const releaseConfigRef = useRef(null);
  const swipeResultRef = useRef({ navigated: false });
  const lastSwipeSnapshotRef = useRef({
    swipeOffset: 0,
    rawSwipeOffset: 0,
    velocity: 0,
    isDragging: false
  });
  const isInternalChangeRef = useRef(false);
  const placeholderPresentedRef = useRef(false);
  const primaryPostIndexRef = useRef(0);
  const primaryPostIdRef = useRef(null);
  const homeReadyAnnouncedRef = useRef(false);

  const announceHomeReady = useCallback(() => {
    if (homeReadyAnnouncedRef.current) {
      return;
    }
    homeReadyAnnouncedRef.current = true;
    if (typeof onHomeReady === 'function') {
      onHomeReady();
    }
  }, [onHomeReady]);

  const easeOutCubic = useCallback((t) => 1 - Math.pow(1 - t, 3), []);

  const cancelTransitionAnimation = useCallback(() => {
    if (transitionAnimationRef.current) {
      cancelAnimationFrame(transitionAnimationRef.current);
      transitionAnimationRef.current = null;
    }
    transitionConfigRef.current = null;
  }, []);

  const cancelReleaseAnimation = useCallback(() => {
    if (releaseAnimationRef.current) {
      cancelAnimationFrame(releaseAnimationRef.current);
      releaseAnimationRef.current = null;
    }
    releaseConfigRef.current = null;
  }, []);

  const completeTransition = useCallback((config) => {
    cancelTransitionAnimation();

    setCurrentPostIndex(config.toIndex);
    setCurrentImageIndex(0);
    setTargetPostIndex(null);
    setIsTransitioning(false);
    setTransitionProgress(0);
    setDragOffsetPercent(0);
    setReleaseOffsetPercent(0);
    setIsImmediateTransition(false);
    swipeResultRef.current = { navigated: false };

    if (onPostChange && posts[config.toIndex]) {
      onPostChange(posts[config.toIndex], config.toIndex);
    }

    // Reset internal change flag after callback
    setTimeout(() => {
      isInternalChangeRef.current = false;
    }, 100);
  }, [cancelTransitionAnimation, onPostChange, posts]);

  const startTransitionAnimation = useCallback((config) => {
    if (!config) return;

    cancelTransitionAnimation();
    cancelReleaseAnimation();

    const clampedInitial = Math.max(0, Math.min(0.95, config.initialProgress || 0));
    const duration = Math.max(200, config.duration || 300);

    transitionConfigRef.current = {
      ...config,
      initialProgress: clampedInitial,
      duration,
      start: null
    };

    setIsTransitioning(true);
    setTargetPostIndex(config.toIndex);
    setTransitionProgress(clampedInitial);

    const step = (timestamp) => {
      const currentConfig = transitionConfigRef.current;
      if (!currentConfig) return;

      if (currentConfig.start === null) {
        currentConfig.start = timestamp;
      }

      const elapsed = timestamp - currentConfig.start;
      const normalized = Math.min(1, elapsed / currentConfig.duration);
      const eased = easeOutCubic(normalized);
      const progress = currentConfig.initialProgress + (1 - currentConfig.initialProgress) * eased;

      setTransitionProgress(progress);

      if (normalized < 1) {
        transitionAnimationRef.current = requestAnimationFrame(step);
      } else {
        completeTransition(currentConfig);
      }
    };

    transitionAnimationRef.current = requestAnimationFrame(step);
  }, [cancelReleaseAnimation, cancelTransitionAnimation, completeTransition, easeOutCubic]);

  const startReleaseAnimation = useCallback((startPercent) => {
    const initial = startPercent || 0;

    if (Math.abs(initial) < 0.001) {
      cancelReleaseAnimation();
      setReleaseOffsetPercent(0);
      return;
    }

    cancelReleaseAnimation();

    releaseConfigRef.current = {
      from: initial,
      start: null,
      duration: 220
    };

    const step = (timestamp) => {
      const cfg = releaseConfigRef.current;
      if (!cfg) return;

      if (cfg.start === null) {
        cfg.start = timestamp;
      }

      const elapsed = timestamp - cfg.start;
      const normalized = Math.min(1, elapsed / cfg.duration);
      const eased = easeOutCubic(normalized);
      const value = cfg.from * (1 - eased);

      setReleaseOffsetPercent(value);

      if (normalized < 1) {
        releaseAnimationRef.current = requestAnimationFrame(step);
      } else {
        cancelReleaseAnimation();
        setReleaseOffsetPercent(0);
      }
    };

    releaseAnimationRef.current = requestAnimationFrame(step);
  }, [cancelReleaseAnimation, easeOutCubic]);

  // AI Regeneration hook
  const {
    isRegenerating: legacyIsRegenerating,
    openRegenerationModal,
    regenerateImage,
    regenerateText
  } = useAIRegeneration();

  // Combined loading and regenerating states
  const isLoading = propIsLoading || apiIsLoading;
  const isRegenerating = legacyIsRegenerating || apiIsRegenerating;

  // Company hook to check DNA status
  const { currentCompany, hasCurrentCompany } = useCompany();
  const { getNavigationStateForScreen, isAccessible } = useCapabilities();
  const hasDNA = useMemo(() => {
    if (!hasCurrentCompany) {
      return false;
    }
    return hasGeneratedDna(currentCompany);
  }, [hasCurrentCompany, currentCompany]);
  const createPostCapability = getNavigationStateForScreen('image-generator');
  const canOpenCreatePost = !createPostCapability.managed || isAccessible(createPostCapability);
  const [hasAnyCampaigns, setHasAnyCampaigns] = useState(Boolean(campaignId));
  const [isCampaignStateReady, setIsCampaignStateReady] = useState(!hasDNA || Boolean(campaignId));

  const postsCount = Array.isArray(posts) ? posts.length : 0;
  const hasAnyPostsReady = !isLoading && postsCount > 0;
  const isEmptyStateReady = !isLoading && postsCount === 0;
  const heroThreadsColor = useMemo(() => [0.98, 0.92, 1], []);

  useEffect(() => {
    if (hasAnyPostsReady || isEmptyStateReady) {
      announceHomeReady();
    }
  }, [hasAnyPostsReady, isEmptyStateReady, announceHomeReady]);

  const renderEmptyActions = (primaryLabel, primaryOnClick) => (
    <div className="mobile-post-empty-actions">
      <button
        className="mobile-post-empty-cta"
        onClick={primaryOnClick}
        disabled={!primaryOnClick}
      >
        {primaryLabel}
      </button>
    </div>
  );

  useEffect(() => {
    let isCancelled = false;

    if (!hasDNA) {
      setHasAnyCampaigns(false);
      setIsCampaignStateReady(true);
      return undefined;
    }

    if (campaignId || postsCount > 0) {
      setHasAnyCampaigns(true);
      setIsCampaignStateReady(true);
      return undefined;
    }

    setIsCampaignStateReady(false);

    const loadCampaignState = async () => {
      try {
        const campaigns = await campaignService.listCampaigns({ limit: 1 });
        if (isCancelled) return;
        setHasAnyCampaigns(Array.isArray(campaigns) && campaigns.length > 0);
      } catch (error) {
        if (isCancelled) return;
        logger.network('Failed to check campaigns for empty state', error);
        setHasAnyCampaigns(false);
      } finally {
        if (!isCancelled) {
          setIsCampaignStateReady(true);
        }
      }
    };

    loadCampaignState();

    return () => {
      isCancelled = true;
    };
  }, [campaignId, hasDNA, postsCount]);

  const emptyStateContent = useMemo(() => {
    if (hasDNA && hasAnyCampaigns) {
      return {
        title: t('postReview.empty.firstPost.title', {
          defaultValue: 'Campaign ready. Create your first post.'
        }),
        description: t('postReview.empty.firstPost.description', {
          defaultValue: 'You already have a campaign but no posts yet. Open Create post to generate the first content.'
        }),
        features: [
          {
            icon: '✍️',
            label: t('postReview.empty.firstPost.features.creation', {
              defaultValue: 'Guided AI creation'
            })
          },
          {
            icon: '🎯',
            label: t('postReview.empty.firstPost.features.alignment', {
              defaultValue: 'Content aligned with your campaign'
            })
          },
          {
            icon: '📅',
            label: t('postReview.empty.firstPost.features.publish', {
              defaultValue: 'Ready to schedule and publish'
            })
          }
        ],
        primaryLabel: t('postReview.empty.firstPost.cta', {
          defaultValue: 'Create post'
        }),
        onPrimary: canOpenCreatePost
          ? () => {
              if (onNavigateToImageGenerator) {
                onNavigateToImageGenerator();
              }
            }
          : null
      };
    }

    if (hasDNA) {
      return {
        title: t('postReview.empty.campaign.title'),
        description: t('postReview.empty.campaign.description'),
        features: [
          {
            icon: '🎯',
            label: t('postReview.empty.campaign.features.strategy')
          },
          {
            icon: '🤖',
            label: t('postReview.empty.campaign.features.authenticAi')
          },
          {
            icon: '📅',
            label: t('postReview.empty.campaign.features.scheduling')
          }
        ],
        primaryLabel: t('postReview.empty.campaign.cta'),
        onPrimary: () => {
          if (onCreateFirstCampaign) {
            onCreateFirstCampaign();
          }
        }
      };
    }
    return {
      title: t('postReview.empty.dna.title'),
      description: t('postReview.empty.dna.description'),
      features: [
        {
          icon: '🧬',
          label: t('postReview.empty.dna.features.identity')
        },
        {
          icon: '🤖',
          label: t('postReview.empty.dna.features.personalAi')
        },
        {
          icon: '✨',
          label: t('postReview.empty.dna.features.authenticContent')
        }
      ],
      primaryLabel: t('postReview.empty.dna.cta'),
      onPrimary: () => {
        if (onNavigateToDna) {
          onNavigateToDna();
        } else if (onCreateFirstCampaign) {
          onCreateFirstCampaign();
        }
      }
    };
  }, [canOpenCreatePost, hasAnyCampaigns, hasDNA, onCreateFirstCampaign, onNavigateToDna, onNavigateToImageGenerator, t]);

  const renderHomeEmptyState = (content) => (
    <div className="mobile-post-review">
      <div className="mobile-post-empty-shell">
        <section className="mobile-empty-hero" aria-live="polite">
          <div className="mobile-empty-glow" aria-hidden="true" />
          <div className="mobile-empty-card">
            <MobileThreadsBackground
              color={heroThreadsColor}
              amplitude={1}
              distance={0.3}
              enableMouseInteraction={false}
              aria-hidden="true"
            />
            <div className="mobile-post-empty mobile-empty-content">
              <div className="mobile-empty-copy">
                <h3 className="mobile-post-empty-title">{content.title}</h3>
                <p className="mobile-post-empty-description">{content.description}</p>
              </div>
              <div className="mobile-post-empty-features">
                {content.features.map((feature, index) => (
                  <div className="mobile-post-empty-feature" key={`${feature.label}-${index}`}>
                    <span className="feature-icon">{feature.icon}</span>
                    <span className="feature-text">{feature.label}</span>
                  </div>
                ))}
              </div>
              {renderEmptyActions(content.primaryLabel, content.onPrimary)}
            </div>
          </div>
        </section>
      </div>
    </div>
  );

  // Handle version selection - LOCAL ONLY (no API call to prevent creating new versions)
  const handleVersionSelect = useCallback((post, versionIndex) => {
    const postId = post?.id || post?._id;
    if (!postId || !postVersions[postId]) {
      logger.user('Version select failed - missing data', {
        postId,
        hasVersions: !!postVersions[postId]
      });
      return;
    }

    const versions = postVersions[postId];
    if (versionIndex < 0 || versionIndex >= versions.length) {
      logger.user('Version select failed - invalid index', {
        versionIndex,
        versionsLength: versions.length
      });
      return;
    }

    const selectedVersion = versions[versionIndex];

    // Check if this is a different version than current
    const currentIndex = currentVersionIndices[postId] || 0;
    if (versionIndex === currentIndex) {
      logger.debug('Version already selected', { postId, versionIndex });
      return;
    }

    logger.debug('Switching to version locally', {
      postId,
      currentIndex,
      newIndex: versionIndex,
      versionId: selectedVersion.version_id,
      contentPreview: selectedVersion.content?.substring(0, 50) + '...'
    });

    // Update current version index (local state only)
    setCurrentVersionIndices(prev => ({
      ...prev,
      [postId]: versionIndex
    }));

    // Update local post state with selected version content (local state only)
    setPosts(prevPosts => prevPosts.map(p => {
      const pId = p?.id || p?._id;
      if (pId === postId) {
        return {
          ...p,
          content: selectedVersion.content,
          description: selectedVersion.content,
          hashtags: selectedVersion.hashtags || p.hashtags,
          // Update media URLs if the version has its own media
          media_urls: selectedVersion.media_urls || p.media_urls,
          images: selectedVersion.images || p.images,
          // Keep the version metadata but don't update current_version_id
          // (that would require an API call which we're avoiding)
        };
      }
      return p;
    }));

    logger.debug('Version switched locally', {
      postId,
      newIndex: versionIndex,
      contentLength: selectedVersion.content?.length
    });
  }, [currentVersionIndices, postVersions]);

  // Handle setting definitive version - the version that will be used for publication
  const handleSetDefinitiveVersion = useCallback(async (post) => {
    const postId = post?.id || post?._id;
    if (!postId) {
      logger.user('Cannot set definitive version - missing post ID');
      return;
    }

    const currentIndex = currentVersionIndices[postId];
    if (currentIndex === undefined || currentIndex === null) {
      logger.user('Cannot set definitive version - no version currently selected', { postId });
      return;
    }

    const versions = postVersions[postId];
    if (!versions || !versions[currentIndex]) {
      logger.user('Cannot set definitive version - version not found', { postId, currentIndex });
      return;
    }

    const selectedVersion = versions[currentIndex];
    const versionId = selectedVersion.version_id;

    if (!versionId) {
      logger.user('Cannot set definitive version - version_id missing', { postId, currentIndex });
      return;
    }

    logger.debug('Setting definitive version', {
      postId,
      versionIndex: currentIndex,
      versionId
    });

    setIsSettingDefinitiveVersion(true);

    try {
      // Call API to activate this version
      const url = `${API_CONFIG.BASE_URL}/api/v1/posts/${postId}/versions/${versionId}/activate`;

      logger.debug('Calling activate version API', { postId, versionId, url });

      const response = await apiFetch(url, {
        method: 'POST'
      });

      if (response.status === 401) {
        logger.warn('Authorization failed while activating version, forcing logout', {
          postId,
          versionId,
          kind: 'network'
        });
        window.dispatchEvent(new CustomEvent('auth-expired'));
        if (authLogout) {
          await authLogout();
        }
        throw new Error('Failed to activate version: 401');
      }

      if (!response.ok) {
        let errorDetails = null;
        try {
          errorDetails = await parseResponseBody(response);
        } catch (parseError) {
          // Ignore JSON parse errors for non-JSON responses
        }
        const errorMessage = errorDetails?.message
          ? `Failed to activate version: ${response.status} - ${errorDetails.message}`
          : `Failed to activate version: ${response.status}`;
        throw new Error(errorMessage);
      }

      let data = null;
      try {
        data = await response.json();
      } catch (parseError) {
        logger.warn('No JSON response while activating version', {
          postId,
          versionId,
          error: parseError,
          kind: 'network'
        });
      }

      logger.debug('Version activated successfully', {
        postId,
        versionId,
        responseKeys: Object.keys(data || {})
      });

      // Update local state with the new current_version_id
      setPosts(prevPosts => prevPosts.map(p => {
        const pId = p?.id || p?._id;
        if (pId === postId) {
          const backendPost = data?.post || {};
          const merged = {
            ...p,
            ...backendPost,
            id: backendPost.id || backendPost._id || pId,
            current_version_id: backendPost.current_version_id || versionId
          };

          return {
            ...merged,
            content: backendPost.content ?? selectedVersion.content ?? merged.content,
            description: backendPost.content ?? selectedVersion.content ?? merged.description,
            hashtags: backendPost.hashtags ?? selectedVersion.hashtags ?? merged.hashtags,
            media_urls: backendPost.media_urls ?? selectedVersion.media_urls ?? merged.media_urls,
            images: backendPost.images ?? selectedVersion.images ?? merged.images
          };
        }
        return p;
      }));

      // Update definitive version index in state
      setDefinitiveVersionIndices(prev => ({
        ...prev,
        [postId]: currentIndex
      }));

    } catch (error) {
      logger.network('Error setting definitive version', error, { postId, versionId });
      // Could show an error toast to the user here
    } finally {
      setIsSettingDefinitiveVersion(false);
    }
  }, [currentVersionIndices, postVersions]);

  // Initialize posts data - use API posts if available, otherwise use props
  useEffect(() => {
    const postsToUse = ((apiPosts && apiPosts.length > 0) && !preferPropPosts) ? apiPosts : (propPosts || []);

    console.log('[MobilePostReview] Posts data update:', {
      apiPostsLength: apiPosts?.length,
      propPostsLength: propPosts?.length,
      preferPropPosts,
      usingApiPosts: (apiPosts && apiPosts.length > 0) && !preferPropPosts
    });

    // Ensure chronological order here as a safeguard (API already sorted)
    const sorted = [...postsToUse].sort((a, b) => {
      const timeA = getPostScheduledTimestamp(a) ?? 0;
      const timeB = getPostScheduledTimestamp(b) ?? 0;
      return timeA - timeB;
    });
    setPosts(sorted);

    // Reset initialized flag when posts array changes significantly
    const postsChanged = !initializedRef.current || sorted.length !== posts.length;
    if (postsChanged && sorted.length > 0) {
      initializedRef.current = false;
    }

    // Set initial index only on first load
    if (!initializedRef.current) {
      let initialIndex = 0;

      // Always try to find the next upcoming post
      if (sorted.length > 0) {
        const now = Date.now();
        const hasUpcomingInSorted = sorted.some(post => {
          const timestamp = getPostScheduledTimestamp(post);
          return typeof timestamp === 'number' && timestamp > now;
        });

        // Find the first post scheduled in the future
        const nextUpcomingIdx = sorted.findIndex(p => {
          const timestamp = getPostScheduledTimestamp(p);
          return typeof timestamp === 'number' && timestamp > now;
        });

        // Use the next upcoming post if found; otherwise use propCurrentIndex or the latest post
        if (nextUpcomingIdx >= 0) {
          initialIndex = nextUpcomingIdx;
        } else if (!hasUpcomingInSorted) {
          initialIndex = sorted.length;
          placeholderPresentedRef.current = true;
        } else if (propCurrentIndex !== undefined) {
          initialIndex = Math.max(0, Math.min(propCurrentIndex, sorted.length - 1));
        } else {
          initialIndex = sorted.length - 1;
        }
      }

      const primaryPost = initialIndex < sorted.length ? sorted[initialIndex] : null;
      primaryPostIndexRef.current = initialIndex;
      primaryPostIdRef.current = getPostIdentifier(primaryPost);

      // If a specific post is requested (from calendar), skip auto-selection but keep primary reference
      if (targetPostId) {
        initializedRef.current = true;
        return;
      }

      setCurrentPostIndex(initialIndex);
      initializedRef.current = true;
      if (onPostChange && primaryPost) {
        onPostChange(primaryPost, initialIndex);
      }
    }

  }, [propPosts, apiPosts, preferPropPosts, onPostChange, propCurrentIndex, targetPostId, getPostScheduledTimestamp]);

  useEffect(() => {
    if (!showNoUpcomingPlaceholder && currentPostIndex >= posts.length && posts.length > 0) {
      setCurrentPostIndex(Math.max(0, posts.length - 1));
    }
  }, [showNoUpcomingPlaceholder, currentPostIndex, posts.length]);

  useEffect(() => {
    if (showNoUpcomingPlaceholder) {
      if (!placeholderPresentedRef.current && currentPostIndex < posts.length) {
        placeholderPresentedRef.current = true;
        setCurrentPostIndex(posts.length);
      }
    } else {
      placeholderPresentedRef.current = false;
    }
  }, [showNoUpcomingPlaceholder, currentPostIndex, posts.length]);

  // Initialize versions from API data (backend manages versions)
  useEffect(() => {
    if (!posts || posts.length === 0) {
      updatePostVersionsState({});
      setCurrentVersionIndices({});
      return;
    }

    const previousVersionsState = postVersionsRef.current;
    const mappedVersions = {};

    posts.forEach(post => {
      const postId = post?.id || post?._id;
      if (!postId) return;

      const apiVersions = post.versions || [];
      if (apiVersions.length > 0) {
        mappedVersions[postId] = sortVersionsDescending(apiVersions);
      }
    });

    updatePostVersionsState(mappedVersions);

    setCurrentVersionIndices(prevIndices => {
      const updatedIndices = {};

      posts.forEach(post => {
        const postId = post?.id || post?._id;
        if (!postId) return;

        const versionsForPost = mappedVersions[postId];
        if (!versionsForPost || versionsForPost.length === 0) {
          return;
        }

        const currentVersionId = post.current_version_id;
        let indexToUse;

        // If we have a previous selection, try to map it to the reordered array
        if (typeof prevIndices?.[postId] === 'number') {
          const previousVersions = previousVersionsState?.[postId];
          const previousVersion = previousVersions && previousVersions[prevIndices[postId]];
          const previousVersionId = getVersionIdentifier(previousVersion);

          if (previousVersionId) {
            const matchingIndex = versionsForPost.findIndex(v => getVersionIdentifier(v) === previousVersionId);
            if (matchingIndex >= 0) {
              indexToUse = matchingIndex;
            }
          }
        }

        // Clamp previous selection if still valid, otherwise fallback to API metadata or most recent
        if (typeof indexToUse !== 'number' || indexToUse < 0 || indexToUse >= versionsForPost.length) {
          if (currentVersionId) {
            const apiCurrentIndex = versionsForPost.findIndex(v => getVersionIdentifier(v) === currentVersionId);
            if (apiCurrentIndex >= 0) {
              indexToUse = apiCurrentIndex;
            }
          }

          if (typeof indexToUse !== 'number' || indexToUse < 0 || indexToUse >= versionsForPost.length) {
            indexToUse = 0;
          }
        }

        updatedIndices[postId] = indexToUse;

        console.log('[MobilePostReview] Versions synced for post:', {
          postId,
          versionsCount: versionsForPost.length,
          currentVersionId,
          selectedIndex: indexToUse,
          versionIds: versionsForPost.map(v => v.version_id)
        });
      });

      return updatedIndices;
    });

    // Initialize definitive version indices from current_version_id
    setDefinitiveVersionIndices(prevDefinitive => {
      const updatedDefinitive = {};

      posts.forEach(post => {
        const postId = post?.id || post?._id;
        if (!postId) return;

        const versionsForPost = mappedVersions[postId];
        if (!versionsForPost || versionsForPost.length === 0) {
          return;
        }

        const currentVersionId = post.current_version_id;
        if (currentVersionId) {
          const definitiveIndex = versionsForPost.findIndex(v => getVersionIdentifier(v) === currentVersionId);
          if (definitiveIndex >= 0) {
            updatedDefinitive[postId] = definitiveIndex;

            console.log('[MobilePostReview] Definitive version set for post:', {
              postId,
              definitiveIndex,
              versionId: currentVersionId
            });
          }
        }
      });

      return updatedDefinitive;
    });

    console.log('[MobilePostReview] Total posts with versions:', Object.keys(mappedVersions).length);
  }, [posts, sortVersionsDescending, updatePostVersionsState, getVersionIdentifier]);

  // When a precise targetPostId is provided, focus that post if present
  useEffect(() => {
    if (!targetPostId || !posts || posts.length === 0) return;
    const normalizedTargetId = getPostIdentifier(targetPostId);
    if (!normalizedTargetId) return;

    const idx = posts.findIndex(p => getPostIdentifier(p) === normalizedTargetId);
    if (idx >= 0) {
      // Use internal state setters directly to avoid referencing navigateToPost before it's defined
      setIsImmediateTransition(true);
      setCurrentPostIndex(idx);
      setCurrentImageIndex(0);
      setDragOffsetPercent(0);
      setReleaseOffsetPercent(0);
      setTransitionProgress(0);
      setTimeout(() => setIsImmediateTransition(false), 50);
      if (onPostChange) onPostChange(posts[idx], idx);
    }
  }, [targetPostId, posts, onPostChange]);

  // Display API errors to user
  useEffect(() => {
    if (apiError) {
      console.error('[MobilePostReview] API Error:', apiError);
      // Here you could show a toast or error message to the user
    }
  }, [apiError]);

  // Listen for post updates from edit page
  useEffect(() => {
    const handlePostUpdate = (event) => {
      console.log('[MobilePostReview] Post updated, refreshing list:', event.detail);
      // Refresh the posts list to show updated data
      if (apiRefresh) {
        apiRefresh();
      }
    };

    window.addEventListener('postUpdated', handlePostUpdate);
    return () => {
      window.removeEventListener('postUpdated', handlePostUpdate);
    };
  }, [apiRefresh]);

  // NEW: Enhanced navigation with smooth transition
  const navigateToPost = useCallback((newIndex, immediate = false, isExternal = false, animationConfig = {}) => {
    if (newIndex < 0 || newIndex >= totalSlides) {
      return;
    }

    if (!isExternal) {
      isInternalChangeRef.current = true;
    }

    const fromIndex = currentPostIndex;

    if (newIndex === fromIndex && !immediate) {
      swipeResultRef.current = { navigated: false };
      isInternalChangeRef.current = false;
      return;
    }

    if (immediate || isTransitioning) {
      cancelTransitionAnimation();
      cancelReleaseAnimation();

      setIsImmediateTransition(true);
      setIsTransitioning(false);
      setTargetPostIndex(null);
      setTransitionProgress(0);
      setDragOffsetPercent(0);
      setReleaseOffsetPercent(0);
      setCurrentPostIndex(newIndex);
      setCurrentImageIndex(0);
      swipeResultRef.current = { navigated: false };

      setTimeout(() => {
        setIsImmediateTransition(false);
      }, 50);

      if (onPostChange && posts[newIndex]) {
        onPostChange(posts[newIndex], newIndex);
      }

      setTimeout(() => {
        isInternalChangeRef.current = false;
      }, 100);

      return;
    }

    const direction = newIndex > fromIndex ? 1 : -1;
    const initialProgress = Math.max(0, Math.min(0.9, animationConfig.initialProgress || 0));
    const velocity = Math.max(0, animationConfig.initialVelocity || 0);
    const duration = Math.max(220, 340 - Math.min(velocity, 2) * 140);

    setDragOffsetPercent(0);
    setReleaseOffsetPercent(0);

    startTransitionAnimation({
      fromIndex,
      toIndex: newIndex,
      direction,
      initialProgress,
      duration
    });
  }, [cancelReleaseAnimation, cancelTransitionAnimation, currentPostIndex, isTransitioning, onPostChange, posts, startTransitionAnimation, totalSlides]);

  const navigateToPrimaryPost = useCallback(() => {
    const totalAvailableSlides = totalSlides;
    if (totalAvailableSlides === 0) {
      return;
    }

    let targetIndex = typeof primaryPostIndexRef.current === 'number'
      ? primaryPostIndexRef.current
      : 0;

    const primaryPostId = primaryPostIdRef.current;
    if (primaryPostId && posts.length > 0) {
      const matchingIndex = posts.findIndex(post => getPostIdentifier(post) === primaryPostId);
      if (matchingIndex >= 0) {
        targetIndex = matchingIndex;
      }
    }

    const maxIndex = totalAvailableSlides - 1;
    if (targetIndex > maxIndex) {
      targetIndex = maxIndex;
    }
    if (targetIndex < 0) {
      targetIndex = 0;
    }

    const targetPost = targetIndex < posts.length ? posts[targetIndex] : null;
    primaryPostIndexRef.current = targetIndex;
    primaryPostIdRef.current = getPostIdentifier(targetPost);

    if (targetIndex === currentPostIndex) {
      return;
    }

    navigateToPost(targetIndex, true, true);
  }, [currentPostIndex, navigateToPost, posts, totalSlides]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleDockPrimaryRequest = () => {
      navigateToPrimaryPost();
    };

    window.addEventListener(DOCK_HOME_PRIMARY_EVENT, handleDockPrimaryRequest);
    return () => {
      window.removeEventListener(DOCK_HOME_PRIMARY_EVENT, handleDockPrimaryRequest);
    };
  }, [navigateToPrimaryPost]);

  // Handle external index changes - Only react to changes from outside the component
  useEffect(() => {
    if (propCurrentIndex !== undefined && posts.length > 0 && !isInternalChangeRef.current) {
      const newIndex = Math.max(0, Math.min(propCurrentIndex, posts.length - 1));
      if (newIndex !== currentPostIndex) {
        const distance = Math.abs(newIndex - currentPostIndex);
        const shouldUseImmediate = distance > 1; // Jump more than 1 post = immediate
        navigateToPost(newIndex, shouldUseImmediate, true); // Mark as external change
      }
    }
  }, [propCurrentIndex, posts.length, currentPostIndex, navigateToPost]);

  // Cleanup any pending animations on unmount
  useEffect(() => {
    return () => {
      cancelTransitionAnimation();
      cancelReleaseAnimation();
    };
  }, [cancelReleaseAnimation, cancelTransitionAnimation]);

  // Get current post
  const currentPost = currentPostIndex < posts.length ? posts[currentPostIndex] : null;

  // Helper function to get thumbnail image (first media - image or video)
  const getThumbnailImage = (post) => {
    return getFirstMediaUrl(post);
  };

  // Helper: extract campaign id from post or prop
  const getPostCampaignId = useCallback((post) => {
    return post?.campaignId || post?.campaign_id || campaignId || null;
  }, [campaignId]);

  // Resolve campaign name for a post from cache/state
  const resolveCampaignNameForPost = useCallback((post) => {
    // Prefer existing name on post if available
    const existing = post?.campaignName || post?.campaign_name;
    if (existing) return existing;
    const cId = getPostCampaignId(post);
    if (!cId) return null;
    return postCampaignNames[cId] || null;
  }, [getPostCampaignId, postCampaignNames]);

  // Fetch campaign names for posts when list changes
  useEffect(() => {
    const fetchCampaignNames = async () => {
      try {
        // Prefill cache from posts if backend already provided names
        (posts || []).forEach(p => {
          const id = getPostCampaignId(p);
          const name = p?.campaignName || p?.campaign_name || null;
          if (id && name && !campaignNameCacheRef.current.has(id)) {
            campaignNameCacheRef.current.set(id, name);
          }
        });
        // Sync state from cache after prefill
        {
          const obj = {};
          campaignNameCacheRef.current.forEach((name, id) => { obj[id] = name; });
          setPostCampaignNames(obj);
        }

        const ids = Array.from(new Set((posts || [])
          .map(p => getPostCampaignId(p))
          .filter(Boolean)));

        const missing = ids.filter(id => !campaignNameCacheRef.current.has(id));
        if (missing.length === 0) {
          // Nothing to fetch; sync state from cache
          const obj = {};
          campaignNameCacheRef.current.forEach((name, id) => { obj[id] = name; });
          setPostCampaignNames(obj);
          return;
        }

        const results = await Promise.all(missing.map(async (id) => {
          try {
            const c = await campaignService.getCampaign(id);
            return { id, name: c?.name || null };
          } catch (e) {
            console.error('[MobilePostReview] Failed to fetch campaign name', { id, error: e });
            return { id, name: null };
          }
        }));

        results.forEach(({ id, name }) => {
          if (name) campaignNameCacheRef.current.set(id, name);
        });

        const obj = {};
        campaignNameCacheRef.current.forEach((name, id) => { obj[id] = name; });
        setPostCampaignNames(obj);
      } catch (e) {
        console.error('[MobilePostReview] Error preparing campaign names', e);
      }
    };

    if (posts && posts.length > 0) {
      fetchCampaignNames();
    }
  }, [posts, getPostCampaignId]);

  // Carousel swipe handlers
  const handleCarouselSwipeStart = useCallback(() => {
    setIsCarouselSwiping(true);
  }, []);

  const handleCarouselSwipeEnd = useCallback(() => {
    setIsCarouselSwiping(false);
  }, []);

  // NEW: Enhanced swipe handlers with progress tracking
  const handleSwipeLeft = useCallback((distance, velocity) => {
    if (isCarouselSwiping || isTransitioning) return;

    const nextIndex = currentPostIndex + 1;
    const screenWidth = window.innerWidth || 1;
    const snapshot = lastSwipeSnapshotRef.current;
    const rawOffset = snapshot.rawSwipeOffset || -distance;
    const swipeOffset = snapshot.swipeOffset || -distance;
    const initialProgress = Math.min(0.9, Math.abs(rawOffset) / screenWidth);
    const swipePercent = (swipeOffset / screenWidth) * 100;
    const velocityHint = snapshot.velocity || velocity;

    if (nextIndex < totalSlides) {
      swipeResultRef.current = { navigated: true };
      navigateToPost(nextIndex, false, false, {
        initialProgress,
        initialVelocity: velocityHint
      });
    } else {
      swipeResultRef.current = { navigated: false };
      startReleaseAnimation(swipePercent);
    }
  }, [currentPostIndex, isCarouselSwiping, isTransitioning, navigateToPost, totalSlides, startReleaseAnimation]);

  const handleSwipeRight = useCallback((distance, velocity) => {
    if (isCarouselSwiping || isTransitioning) return;

    const prevIndex = currentPostIndex - 1;
    const screenWidth = window.innerWidth || 1;
    const snapshot = lastSwipeSnapshotRef.current;
    const rawOffset = snapshot.rawSwipeOffset || distance;
    const swipeOffset = snapshot.swipeOffset || distance;
    const initialProgress = Math.min(0.9, Math.abs(rawOffset) / screenWidth);
    const swipePercent = (swipeOffset / screenWidth) * 100;
    const velocityHint = snapshot.velocity || velocity;

    if (prevIndex >= 0) {
      swipeResultRef.current = { navigated: true };
      navigateToPost(prevIndex, false, false, {
        initialProgress,
        initialVelocity: velocityHint
      });
    } else {
      swipeResultRef.current = { navigated: false };
      startReleaseAnimation(swipePercent);
    }
  }, [currentPostIndex, isCarouselSwiping, isTransitioning, navigateToPost, startReleaseAnimation]);

  // NEW: Handle continuous swipe updates
  const handleSwipeUpdate = useCallback((swipeState) => {
    if (!isCarouselSwiping && !isTransitioning) {
      const screenWidth = window.innerWidth || 1;
      const percent = (swipeState.swipeOffset / screenWidth) * 100;

      setDragOffsetPercent(swipeState.isDragging ? percent : 0);
      lastSwipeSnapshotRef.current = {
        swipeOffset: swipeState.swipeOffset,
        rawSwipeOffset: swipeState.rawSwipeOffset,
        velocity: swipeState.velocity,
        isDragging: swipeState.isDragging
      };
      if (swipeState.isDragging) {
        cancelReleaseAnimation();
        setReleaseOffsetPercent(0);
      }
    }
  }, [cancelReleaseAnimation, isCarouselSwiping, isTransitioning]);

  // Initialize post-level swipe with enhanced options
  const postSwipeProps = useMobileSwipe(
    handleSwipeLeft, 
    handleSwipeRight, 
    {
      threshold: 60, // Slightly lower threshold for more responsive feel
      velocityThreshold: 0.4,
      disabled: isCarouselSwiping || isTransitioning,
      onSwipeUpdate: handleSwipeUpdate, // NEW: Real-time updates
      resistanceStiffness: 0.4, // More resistance for better feel
      returnAnimationDuration: 300,
      preventDefaultTouchmove: false
    }
  );

  // Filter touch event props for DOM (fix React warnings)
  const touchEventProps = {
    onTouchStart: postSwipeProps.onTouchStart,
    onTouchMove: postSwipeProps.onTouchMove,
    onTouchEnd: postSwipeProps.onTouchEnd
  };

  const swipeDirectionClass = postSwipeProps.isDragging
    ? (postSwipeProps.swipeDirection === 'left' ? 'swipe-left' : 'swipe-right')
    : (Math.abs(releaseOffsetPercent) > 0.1 ? (releaseOffsetPercent < 0 ? 'swipe-left' : 'swipe-right') : '');

  const reviewClasses = [
    'mobile-post-review',
    postSwipeProps.isDragging ? 'swiping' : '',
    postSwipeProps.isDragging && postSwipeProps.isHorizontalSwipe ? 'swiping-horizontal' : '',
    isTransitioning ? 'transitioning' : '',
    swipeDirectionClass
  ].filter(Boolean).join(' ');

  // Detect release events to trigger snap-back when swipe is cancelled
  useEffect(() => {
    const wasDragging = lastSwipeSnapshotRef.current.isDragging;
    const isCurrentlyDragging = postSwipeProps.isDragging;

    if (wasDragging && !isCurrentlyDragging) {
      if (!isTransitioning && !isCarouselSwiping) {
        setDragOffsetPercent(0);

        if (swipeResultRef.current.navigated) {
          setReleaseOffsetPercent(0);
          swipeResultRef.current = { navigated: false };
        } else {
          const screenWidth = window.innerWidth || 1;
          const percent = (lastSwipeSnapshotRef.current.swipeOffset / screenWidth) * 100;
          startReleaseAnimation(percent);
          swipeResultRef.current = { navigated: false };
        }
      } else {
        setDragOffsetPercent(0);
        if (!isTransitioning) {
          setReleaseOffsetPercent(0);
        }
        swipeResultRef.current = { navigated: false };
      }
    }

    lastSwipeSnapshotRef.current = {
      ...lastSwipeSnapshotRef.current,
      isDragging: isCurrentlyDragging
    };
  }, [postSwipeProps.isDragging, isTransitioning, isCarouselSwiping, startReleaseAnimation]);

  // Block body scroll during horizontal swipe
  useEffect(() => {
    const isHorizontalSwiping = postSwipeProps.isDragging && postSwipeProps.isHorizontalSwipe;

    if (isHorizontalSwiping) {
      // Store current scroll position
      const scrollY = window.scrollY;

      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      // Restore body scroll
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';

      // Restore scroll position
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [postSwipeProps.isDragging, postSwipeProps.isHorizontalSwipe]);

  // Handle image carousel changes
  const handleImageIndexChange = useCallback((newIndex) => {
    setCurrentImageIndex(newIndex);
  }, []);

  // Handle image click (could open fullscreen)
  const handleImageClick = useCallback((imageUrl, index) => {
    // Could implement fullscreen image viewer here
  }, []);

  // Handle post approval
  const handlePostApprove = useCallback(async (post) => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      console.log('[MobilePostReview] Approving post:', {
        postId: post?.id || post?._id,
        status: post?.status,
        campaignId
      });

      // Try API first, then fallback to prop function
      if (apiApprovePost) {
        // Call API approval (campaignId is optional in the hook)
        await apiApprovePost(post);

        // Update local state immediately to reflect the change
        // The hook updates its own state, but we need to sync our local state too
        setPosts(prevPosts => prevPosts.map(p => {
          const postId = post?.id || post?._id;
          const pId = p?.id || p?._id;
          return pId === postId ? { ...p, status: 'scheduled' } : p;
        }));

        console.log('[MobilePostReview] Post approved successfully, local state updated');
      } else if (onPostApprove) {
        await onPostApprove(post, currentPostIndex);
      }

    } catch (error) {
      console.error('[MobilePostReview] Error approving post:', error);
      // Clear API error if it was set
      if (apiClearError) apiClearError();
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, currentPostIndex, onPostApprove, apiApprovePost, campaignId, apiClearError]);

  // Handle post rejection
  const handlePostReject = useCallback(async (post) => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      console.log('[MobilePostReview] Rejecting post:', {
        postId: post?.id || post?._id,
        status: post?.status,
        campaignId
      });

      // Try API first, then fallback to prop function
      if (apiRejectPost) {
        // Call API rejection (campaignId is optional in the hook)
        await apiRejectPost(post);

        // Update local state immediately to reflect the change
        // The hook updates its own state, but we need to sync our local state too
        setPosts(prevPosts => prevPosts.map(p => {
          const postId = post?.id || post?._id;
          const pId = p?.id || p?._id;
          return pId === postId ? { ...p, status: 'draft' } : p;
        }));

        console.log('[MobilePostReview] Post rejected successfully, local state updated');
      } else if (onPostReject) {
        await onPostReject(post, currentPostIndex);
      }

    } catch (error) {
      console.error('[MobilePostReview] Error rejecting post:', error);
      // Clear API error if it was set
      if (apiClearError) apiClearError();
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing, currentPostIndex, onPostReject, apiRejectPost, campaignId, apiClearError]);

  const handlePostDelete = useCallback(async (post) => {
    if (isProcessing) return;

    const postId = post?.id || post?._id;
    if (!postId) {
      console.warn('[MobilePostReview] Unable to delete post - missing identifier');
      return;
    }

    const deletionIndex = posts.findIndex(p => (p?.id || p?._id) === postId);

    setIsProcessing(true);

    try {
      let deletionHandled = false;

      if (apiDeletePost) {
        await apiDeletePost(post);
        deletionHandled = true;
      } else if (onPostDelete) {
        await onPostDelete(post, currentPostIndex);
        deletionHandled = true;
      }

      if (!deletionHandled) {
        console.warn('[MobilePostReview] No delete handler available - skipping state update');
        return;
      }

      const updatedPosts = posts.filter(p => (p?.id || p?._id) !== postId);
      setPosts(updatedPosts);

      updatePostVersionsState(prev => {
        if (!prev || !prev[postId]) return prev;
        const next = { ...prev };
        delete next[postId];
        return next;
      });

      setCurrentVersionIndices(prev => {
        if (!prev || !(postId in prev)) return prev;
        const next = { ...prev };
        delete next[postId];
        return next;
      });

      const nextIndex = (() => {
        if (updatedPosts.length === 0) {
          return 0;
        }

        if (deletionIndex === -1) {
          return Math.min(currentPostIndex, updatedPosts.length - 1);
        }

        if (currentPostIndex > deletionIndex) {
          return Math.max(0, currentPostIndex - 1);
        }

        if (currentPostIndex === deletionIndex) {
          return Math.min(deletionIndex, updatedPosts.length - 1);
        }

        return currentPostIndex;
      })();

      setCurrentPostIndex(nextIndex);
      setCurrentImageIndex(0);
      setTargetPostIndex(null);
      setDragOffsetPercent(0);
      setReleaseOffsetPercent(0);
      setTransitionProgress(0);

      if (updatedPosts.length > 0 && onPostChange) {
        onPostChange(updatedPosts[nextIndex], nextIndex);
      }

      console.log('[MobilePostReview] Post deleted successfully:', { postId, nextIndex });
    } catch (error) {
      console.error('[MobilePostReview] Error deleting post:', error);
      if (apiClearError) apiClearError();
    } finally {
      setIsProcessing(false);
    }
  }, [
    isProcessing,
    posts,
    apiDeletePost,
    onPostDelete,
    currentPostIndex,
    onPostChange,
    apiClearError,
    updatePostVersionsState
  ]);

  // AI Modal handlers
  const openAIModal = useCallback((type, imageIndex = null) => {
    
    setAiModalType(type);
    setAiModalImageIndex(imageIndex);
    setAiModalOpen(true);
  }, []);

  const closeAIModal = useCallback(() => {
    
    setAiModalOpen(false);
    setAiModalType('full');
    setAiModalImageIndex(null);
  }, []);

  // Handle AI regeneration from modal - backend creates new version automatically
  const handleAIRegeneration = useCallback(async (regenerationData) => {
    const { type, prompt, post, imageIndex } = regenerationData;
    const postId = post?.id || post?._id;

    try {
      console.log('[MobilePostReview] Starting AI regeneration:', { type, postId });

      if (apiRegeneratePost) {
        const options = {
          user_prompt: prompt || "Regenerate this post with fresh content"
        };

        // Add image-specific options
        if (type === 'image' && imageIndex !== null) {
          options.imageIndex = imageIndex;
        }

        // Call API regeneration - backend creates new version automatically
        const response = await apiRegeneratePost(post, type, options);

        // The response includes the updated post with new version in the versions array
        const updatedPost = response.post;

        if (updatedPost) {
          // Update local state with new post data and versions
          setPosts(prevPosts => prevPosts.map(p => {
            const pId = p?.id || p?._id;
            if (pId === postId) {
              return {
                ...p,
                ...updatedPost,
                id: postId
              };
            }
            return p;
          }));

          // Update versions state with new versions from API
          if (updatedPost.versions) {
            const sortedVersions = sortVersionsDescending(updatedPost.versions || []);

            updatePostVersionsState(prev => ({
              ...prev,
              [postId]: sortedVersions
            }));

            // Set to most recent version (index 0 after sorting)
            setCurrentVersionIndices(prev => ({
              ...prev,
              [postId]: 0
            }));
          }

          console.log('[MobilePostReview] Regeneration successful, new version created');
        }
      } else if (onPostRegenerate) {
        // Fallback to prop function
        const options = imageIndex !== null ? { imageIndex, prompt } : { prompt };
        await onPostRegenerate(post, type, options);
      } else {
        // Fallback to legacy hook
        if (type === 'image') {
          await regenerateImage(post.id, { imageIndex, prompt });
        } else if (type === 'text') {
          await regenerateText(post.id, { prompt });
        } else {
          await regenerateText(post.id, { prompt });
        }
      }

      // Close modal after successful regeneration
      closeAIModal();

    } catch (error) {
      console.error('[MobilePostReview] Error with AI regeneration:', error);
      // Clear API error if it was set
      if (apiClearError) apiClearError();
      // Keep modal open on error
    }
  }, [
    onPostRegenerate,
    regenerateImage,
    regenerateText,
    closeAIModal,
    apiRegeneratePost,
    apiClearError,
    sortVersionsDescending,
    updatePostVersionsState
  ]);

  // Legacy handlers for backward compatibility
  const handleRegenerateImage = useCallback((imageIndex) => {
    openAIModal('image', imageIndex);
  }, [openAIModal]);

  const handleRegenerateDescription = useCallback(() => {
    openAIModal('text');
  }, [openAIModal]);

  // Handle post regeneration from PostInfo component
  const handlePostRegenerate = useCallback((post, type) => {
    openAIModal(type);
  }, [openAIModal]);

  // Text Edit Modal handlers
  const openTextEditModal = useCallback(() => {
    
    setTextEditModalOpen(true);
  }, []);

  const closeTextEditModal = useCallback(() => {
    
    setTextEditModalOpen(false);
  }, []);

  // Handle text save from modal
  const handleTextSave = useCallback(async (newText) => {
    setIsTextSaving(true);
    
    try {
      // Try API first, then fallback to prop function
      if (apiEditPostText) {
        await apiEditPostText(currentPost, newText);
      } else if (onPostTextEdit) {
        await onPostTextEdit(currentPost, newText);
      } else {
        // Update local state if no external handler
        setPosts(prevPosts => 
          prevPosts.map(post => 
            post.id === currentPost.id 
              ? { ...post, description: newText, content: newText }
              : post
          )
        );
      }
      
      // Close modal after successful save
      closeTextEditModal();
      
    } catch (error) {
      console.error('[MobilePostReview] Error saving text:', error);
      // Clear API error if it was set
      if (apiClearError) apiClearError();
      // Keep modal open on error
    } finally {
      setIsTextSaving(false);
    }
  }, [currentPost, onPostTextEdit, closeTextEditModal, apiEditPostText, campaignId, apiClearError]);

  // Handle upload image
  const handleUploadImage = useCallback((imageIndex) => {
    console.log('[MobilePostReview] Handling image upload for index:', imageIndex);
    
    if (apiHandleImageUpload) {
      // Use API-based upload
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = SUPPORTED_MEDIA_ACCEPT;
      input.onchange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) {
          return;
        }

        if (!isSupportedMediaFile(file)) {
          notify({
            variant: 'error',
            title: unsupportedFormatMessage
          });
          return;
        }

        try {
          await apiHandleImageUpload(currentPost, imageIndex, file);
          console.log('[MobilePostReview] Image uploaded successfully via API');
        } catch (error) {
          console.error('[MobilePostReview] API image upload failed:', error);
          if (apiClearError) apiClearError();
        }
      };
      input.click();
    } else if (onImageUpload) {
      onImageUpload(currentPost, imageIndex);
    } else {
      // Fallback - basic file picker without upload
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = SUPPORTED_MEDIA_ACCEPT;
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (!file) {
          return;
        }

        if (!isSupportedMediaFile(file)) {
          notify({
            variant: 'error',
            title: unsupportedFormatMessage
          });
          return;
        }

        console.log('[MobilePostReview] File selected (no upload handler):', file.name);
        // Could show a message to user that upload needs to be implemented
      };
      input.click();
    }
  }, [apiClearError, apiHandleImageUpload, campaignId, currentPost, notify, onImageUpload, unsupportedFormatMessage]);

  // Skeleton layout while loading
  const renderLoadingSkeleton = () => {
    return (
      <div className="mobile-post-review-container">
        <div className="mobile-post-review">
          <div className="mobile-post-slides-container">
            <div className="mobile-post-slide active" style={{ position: 'relative' }}>
              <div className="mobile-post-content">
                {/* Header skeleton */}
                <div className="mobile-post-info-header">
                  <div className="mobile-post-info-content" style={{ alignItems: 'center' }}>
                    <Skeleton variant="rounded" width={50} height={50} shimmer />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Skeleton width="60%" height={16} shimmer style={{ marginBottom: 6 }} />
                      <Skeleton width="40%" height={12} shimmer />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Skeleton width={28} height={28} variant="circle" shimmer />
                    <Skeleton width={28} height={28} variant="circle" shimmer />
                  </div>
                </div>

                {/* Image carousel skeleton with 4:5 aspect ratio */}
                <div className="mobile-post-image-container" style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', inset: 0 }}>
                    <Skeleton width="100%" height="100%" shimmer variant="rounded" />
                  </div>
                </div>

                {/* Description skeleton */}
                <div className="mobile-post-description-container">
                  <div className="mobile-post-description-header">
                    <Skeleton width="40%" height={18} shimmer />
                  </div>
                  <div className="mobile-post-description">
                    <Skeleton count={3} width="100%" height={12} shimmer />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const noUpcomingTitle = t('postReview.empty.noUpcoming.title', {
    defaultValue: 'Non ci sono post programmabili al momento'
  });

  const noUpcomingSubtitle = t('postReview.empty.noUpcoming.subtitle', {
    defaultValue: 'Programma una nuova campagna per mantenere attivo il tuo calendario editoriale.'
  });

  const noUpcomingCta = t('postReview.empty.noUpcoming.cta', {
    defaultValue: 'Nuova Campagna'
  });

  const noUpcomingHint = t('postReview.empty.noUpcoming.hint', {
    defaultValue: 'Fai swipe per vedere i post passati'
  });

  const handleNoUpcomingCampaignClick = useCallback(() => {
    if (onOpenNewCampaignModal) {
      onOpenNewCampaignModal();
      return;
    }

    if (onCreateFirstCampaign) {
      onCreateFirstCampaign();
    }
  }, [onOpenNewCampaignModal, onCreateFirstCampaign]);

  // Loading state with skeletons
  if (isLoading) {
    return renderLoadingSkeleton();
  }

  if (!isLoading && hasDNA && !isCampaignStateReady && postsCount === 0) {
    return renderLoadingSkeleton();
  }

  // Empty state - Welcome message for new users
  if (!posts || posts.length === 0) {
    return renderHomeEmptyState(emptyStateContent);
  }

  // NEW: Enhanced posts rendering with fluid transitions
  const getPostsToRender = () => {
    const postsToRender = [];
    const effectiveIndex = targetPostIndex !== null ? targetPostIndex : currentPostIndex;

    const pushSlide = (slideIndex) => {
      if (slideIndex < 0 || slideIndex >= totalSlides) {
        return;
      }

      const isNoUpcoming = showNoUpcomingPlaceholder && slideIndex === posts.length;
      postsToRender.push({
        post: isNoUpcoming ? null : posts[slideIndex],
        index: slideIndex,
        isActive: slideIndex === currentPostIndex,
        isTarget: slideIndex === targetPostIndex,
        isNoUpcoming
      });
    };

    if (isImmediateTransition) {
      pushSlide(currentPostIndex);
    } else {
      for (let i = effectiveIndex - 1; i <= effectiveIndex + 1; i++) {
        pushSlide(i);
      }
    }

    return postsToRender;
  };

  // NEW: Enhanced post slide rendering with fluid animation
  const renderPostSlide = ({ post, index, isActive, isTarget, isNoUpcoming }) => {
    const postIdentifier = isNoUpcoming
      ? 'no-upcoming'
      : (post?.id || post?._id || post?.post_id || `post-${index}`);
    const carouselLoadingScope = `carousel-${postIdentifier}`;
    const versionsLoadingScope = `versions-${postIdentifier}`;
    const carouselAspect = isNoUpcoming ? null : getCarouselAspectForPost(post);
    const carouselWrapperStyle = carouselAspect?.style || DEFAULT_CAROUSEL_STYLE;
    const deltaIndex = targetPostIndex !== null ? targetPostIndex - currentPostIndex : 0;
    const isAnimatingTransition = isTransitioning && targetPostIndex !== null;

    // Base offset anchored to current index; adjust with transition progress when active
    let baseOffset = (index - currentPostIndex) * 100;
    if (isAnimatingTransition) {
      baseOffset = (index - currentPostIndex - deltaIndex * transitionProgress) * 100;
    }

    // Apply live drag offset or release snap-back offset when not transitioning
    const activeDragOffset = postSwipeProps.isDragging && !isCarouselSwiping ? dragOffsetPercent : 0;
    const activeReleaseOffset = !postSwipeProps.isDragging && !isAnimatingTransition ? releaseOffsetPercent : 0;

    const totalOffset = baseOffset + activeDragOffset + activeReleaseOffset;
    
    // Calculate opacity for smooth fade effect
    let opacity = 1;
    const absOffset = Math.abs(totalOffset);
    if (absOffset > 50) {
      opacity = Math.max(0.3, 1 - (absOffset - 50) / 100);
    }
    
    // Calculate scale for subtle depth effect
    const scale = absOffset > 0 ? Math.max(0.95, 1 - absOffset / 1000) : 1;

    const slideKey = `post-${postIdentifier}-${index}`;
    const slideClasses = [
      'mobile-post-slide',
      isActive ? 'active' : '',
      postSwipeProps.isDragging ? 'dragging' : '',
      isTransitioning ? 'transitioning' : ''
    ].filter(Boolean).join(' ');

    const slideStyle = {
      transform: `translateX(${totalOffset}%) scale(${scale})`,
      opacity,
      transition: postSwipeProps.isDragging || isTransitioning ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s ease',
      zIndex: isActive ? 3 : (Math.abs(index - currentPostIndex) === 1 ? 2 : 1)
    };

    const shouldShowNoUpcomingPage = isNoUpcoming;

    return (
      <div 
        key={slideKey}
        className={slideClasses}
        style={slideStyle}
      >
        {shouldShowNoUpcomingPage ? (
          <div className="mobile-no-upcoming-wrapper" {...touchEventProps}>
            <div className="mobile-post-empty">
              <h3 className="mobile-post-empty-title">{noUpcomingTitle}</h3>
              <p className="mobile-post-empty-description">{noUpcomingSubtitle}</p>
              <button
                className="mobile-post-empty-cta"
                onClick={handleNoUpcomingCampaignClick}
              >
                {noUpcomingCta}
              </button>
              <div className="mobile-post-empty-hint">
                <span>{noUpcomingHint}</span>
                <img
                  src={swipeBackIcon}
                  alt=""
                  aria-hidden="true"
                  className="mobile-post-empty-swipe-icon"
                />
              </div>
            </div>
          </div>
        ) : (
          /* Main Content */
          <div className="mobile-post-content">
            {/* Post Info Header - Spostato all'interno per un layout più elegante */}
            <div {...(isActive ? touchEventProps : {})}>
              <PostInfo
                post={post}
                currentIndex={index}
                totalPosts={posts.length}
                campaignName={resolveCampaignNameForPost(post)}
                thumbnailImage={getThumbnailImage(post)}
                allPosts={posts}
                onApprove={isActive ? handlePostApprove : () => {}}
                onReject={isActive ? handlePostReject : () => {}}
                onDeletePost={isActive ? handlePostDelete : () => {}}
                onAIRegenerate={isActive ? handlePostRegenerate : () => {}}
                onEditPost={isActive ? () => {
                  console.log('[MobilePostReview] Edit requested for post:', {
                    postId: post?.id,
                    postIndex: index,
                    isActive,
                    postData: post
                  });
                  if (onEditPost) {
                    onEditPost(post);
                  } else {
                    console.log('[MobilePostReview] No edit handler provided');
                  }
                } : () => {}}
                onCampaignClick={isActive && onCampaignClick ? onCampaignClick : undefined}
                onThumbnailClick={isActive && onThumbnailClick ? () => onThumbnailClick(post) : undefined}
                isApproved={post?.status === 'scheduled'}
                isProcessing={isProcessing && isActive}
                isRegenerating={isRegenerating && isActive}
              />
            </div>
            {/* Image Carousel */}
            <div
              className="mobile-carousel-responsive"
              style={carouselWrapperStyle}
            >
              <PostCarousel 
                images={getAllMediaUrls(post)}
                currentIndex={isActive ? currentImageIndex : 0}
                onIndexChange={isActive ? handleImageIndexChange : () => {}}
                onImageClick={isActive ? handleImageClick : () => {}}
                onRegenerateImage={isActive ? handleRegenerateImage : () => {}}
                onUploadImage={isActive ? handleUploadImage : () => {}}
                isRegenerating={isRegenerating && isActive}
                onSwipeStart={isActive ? handleCarouselSwipeStart : () => {}}
                onSwipeEnd={isActive ? handleCarouselSwipeEnd : () => {}}
                disabled={!isActive} // Disable carousel interactions for non-active posts
                isPostActive={isActive && !isTransitioning} // Video should only play when post is truly active
                postSwipeHandlers={isActive ? postSwipeProps : null}
                getImageLoadingProps={getImageLoadingProps}
                imageLoadingScope={carouselLoadingScope}
              />
            </div>

          {/* Post Description */}
          <div
            className="mobile-post-description-container"
            {...(isActive ? touchEventProps : {})}
          >
            {/* Description Header */}
            <div className="mobile-post-description-header">
              <h3 className="mobile-post-description-title">{t('postReview.details.descriptionTitle')}</h3>
            </div>

            {/* Description Text */}
            <div className="mobile-post-description mobile-editable-text">
              {post?.content || post?.description || t('postReview.details.emptyDescription')}
            </div>
          </div>

          {/* Post Versions - Only show for active post */}
          {isActive && postVersions[post?.id || post?._id] && (
            <PostVersions
              versions={postVersions[post?.id || post?._id]}
              currentVersionIndex={currentVersionIndices[post?.id || post?._id] || 0}
              definitiveVersionIndex={definitiveVersionIndices[post?.id || post?._id]}
              onVersionSelect={(versionIndex) => handleVersionSelect(post, versionIndex)}
              onSetDefinitiveVersion={() => handleSetDefinitiveVersion(post)}
              isSettingDefinitive={isSettingDefinitiveVersion}
              parentPost={post}
              postSwipeHandlers={postSwipeProps}
              getImageLoadingProps={getImageLoadingProps}
              imageLoadingScope={versionsLoadingScope}
            />
          )}
        </div>
        )}
      </div>
    );
  };

  // Main render
  return (
    <>
      { (isLoading || posts.length === 0) ? (
        <div className="mobile-post-review-container">
          <div 
            ref={containerRef}
            className="mobile-post-review"
          >
            <div className="mobile-post-slides-container">
              <div className="mobile-post-slide active">
                <div className="mobile-post-content">
                  {/* Header skeleton */}
                  <div className="mobile-post-info-header">
                    <div className="mobile-post-info-content">
                      <div className="mobile-post-thumbnail-container">
                        <Skeleton variant="rounded" width={50} height={50} animation="wave" shimmer />
                      </div>
                      <div className="mobile-post-meta" style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <Skeleton width="60%" height={14} shimmer />
                        <Skeleton width="40%" height={12} shimmer />
                        <Skeleton width="30%" height={12} shimmer />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Skeleton variant="circle" width={32} height={32} />
                      <Skeleton variant="circle" width={32} height={32} />
                    </div>
                  </div>

                  {/* Image area skeleton */}
                  <div className="mobile-post-image-container">
                    <div className="mobile-carousel-images-container">
                      <Skeleton variant="rounded" width="100%" height="100%" animation="wave" shimmer />
                    </div>
                  </div>

                  {/* Description skeleton */}
                  <div className="mobile-post-description-container">
                    <div className="mobile-post-description-header">
                      <Skeleton width="40%" height={18} />
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <Skeleton count={3} height={14} shimmer />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="mobile-post-review-container">
          <div
            ref={containerRef}
            className={reviewClasses}
          >
            <div className="mobile-post-slides-container">
              {getPostsToRender().map(renderPostSlide)}
            </div>
          </div>
        </div>
      )}

      {/* AI Regeneration Modal */}
      <AIRegenerationModal
        isOpen={aiModalOpen}
        onClose={closeAIModal}
        type={aiModalType}
        post={currentPost}
        imageIndex={aiModalImageIndex}
        onRegenerate={handleAIRegeneration}
        isRegenerating={isRegenerating}
      />

      {/* Text Edit Modal */}
      <TextEditModal
        isOpen={textEditModalOpen}
        onClose={closeTextEditModal}
        text={currentPost?.content || currentPost?.description || ''}
        onSave={handleTextSave}
        isSaving={isTextSaving}
      />
    </>
  );
};

export default MobilePostReview;
